import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, Grid, TextField, Button, Typography, CircularProgress, Switch, FormControlLabel, Checkbox, Accordion, AccordionSummary, AccordionDetails, FormGroup } from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon, ExpandMore } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { ROLE_NAME_PREFIX, roleDisplayStem } from '../../utils/rbacDisplay';

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({ permissions: [], grouped: {} });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    permission_ids: [],
    reference_code: '',
  });

  useEffect(() => { fetchPermissions(); if (isEdit) fetchRole(); }, [id]);

  const fetchPermissions = async () => { try { const r = await api.get('/roles/permissions'); setPermissions(r.data.data); } catch (e) {} };

  const fetchRole = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/roles/${id}`);
      const role = r.data.data;
      const rolePerms = role.permissions || role.Permissions || [];
      setFormData({
        ...role,
        name: roleDisplayStem(role.name),
        permission_ids: rolePerms.map((p) => p.id),
        reference_code: role.reference_code || '',
      });
    } catch (e) { toast.error('Failed to fetch'); navigate('/admin/roles'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permId)
        ? prev.permission_ids.filter(id => id !== permId)
        : [...prev.permission_ids, permId]
    }));
  };

  const toggleModule = (moduleName) => {
    const modulePermIds = permissions.grouped[moduleName]?.map(p => p.id) || [];
    const allSelected = modulePermIds.every(id => formData.permission_ids.includes(id));
    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePermIds.includes(id))
        : [...new Set([...prev.permission_ids, ...modulePermIds])]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { reference_code: _ref, ...rest } = formData;
      const payload = {
        name: rest.name,
        description: rest.description,
        is_active: rest.is_active,
        permission_ids: rest.permission_ids,
      };
      if (isEdit) await api.put(`/roles/${id}`, payload);
      else await api.post('/roles', payload);
      toast.success(`Role ${isEdit ? 'updated' : 'created'}`);
      navigate('/admin/roles');
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/roles')}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Role' : 'New Role'}</Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Role Information</Typography>
            <Grid container spacing={3}>
              {isEdit && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Reference" value={formData.reference_code || '—'} disabled />
                </Grid>
              )}
              <Grid item xs={12} md={isEdit ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Role name (short)"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  helperText={`Stored as ${ROLE_NAME_PREFIX}{name}`}
                />
              </Grid>
              <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />} label="Active" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Description" name="description" value={formData.description || ''} onChange={handleChange} multiline rows={2} /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Permissions</Typography>
            {Object.entries(permissions.grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([module, perms]) => (
              <Accordion key={module}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Checkbox checked={perms.every(p => formData.permission_ids.includes(p.id))}
                      indeterminate={perms.some(p => formData.permission_ids.includes(p.id)) && !perms.every(p => formData.permission_ids.includes(p.id))}
                      onChange={() => toggleModule(module)} onClick={(e) => e.stopPropagation()} />
                    <Typography sx={{ fontWeight: 600 }}>
                      {module.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">({perms.filter(p => formData.permission_ids.includes(p.id)).length}/{perms.length})</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup row>
                    {perms.map((p) => (
                      <FormControlLabel
                        key={p.id}
                        control={<Checkbox checked={formData.permission_ids.includes(p.id)} onChange={() => togglePermission(p.id)} />}
                        label={p.name || p.code}
                        sx={{ width: 'auto', maxWidth: 220 }}
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/admin/roles')}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>{saving ? 'Saving...' : 'Save Role'}</Button>
        </Box>
      </form>
    </Box>
  );
};

export default RoleForm;
